import { useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { OnboardingShell } from '../OnboardingShell';
import { SPORTSBOOKS } from '../../../utils/constants';
import { LogoGenerator } from '../../../components/LogoGenerator';

export const SportsbooksStep = (props) => {
	const { stepIndex, totalSteps, onBack, onNext } = props;
	const { getAccessTokenSilently } = useAuth0();

	const [selected, setSelected] = useState([]);
	const [query, setQuery] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return SPORTSBOOKS;
		return SPORTSBOOKS.filter((s) => s.label.toLowerCase().includes(q));
	}, [query]);

	const toggle = (value) => {
		setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
	};

	const selectAll = () => setSelected(SPORTSBOOKS.map((s) => s.value));
	const clearAll = () => setSelected([]);

	const handleContinue = async () => {
		if (submitting) return;
		setSubmitting(true);
		try {
			if (selected.length > 0) {
				const token = await getAccessTokenSilently();
				const userdb = JSON.parse(window.localStorage.getItem('userdb'));
				await axios.post(`${process.env.REACT_APP_BACKEND_URL}/set_preferred_sportsbooks?email=${userdb.email}`, selected, {
					headers: { Authorization: `Bearer ${token}` },
				});
				const copy = JSON.parse(window.localStorage.getItem('userdb'));
				copy.preferred_sportsbooks = selected;
				window.localStorage.setItem('userdb', JSON.stringify(copy));
			}
		} catch (e) {
			console.error('set_preferred_sportsbooks failed', e);
		} finally {
			setSubmitting(false);
			onNext();
		}
	};

	return (
		<OnboardingShell
			stepIndex={stepIndex}
			totalSteps={totalSteps}
			title="Pick your sportsbooks"
			subtitle="Choose the books you bet at so we can highlight the bets that matter to you. You can change this anytime."
			onBack={onBack}
			canContinue={selected.length > 0 && !submitting}
			onContinue={handleContinue}
			continueLabel={submitting ? 'Saving...' : `Continue${selected.length ? ` (${selected.length})` : ''}`}
			onSkip={onNext}
			skipLabel="Skip for now">
			<div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2">
				<input
					className="ob-search flex-1"
					type="text"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search sportsbooks..."
				/>
				<div className="flex items-center gap-3 sm:ml-2 text-xs">
					<span onClick={selectAll} className="ob-link">
						Select all
					</span>
					{selected.length > 0 && (
						<span onClick={clearAll} className="ob-link">
							Clear
						</span>
					)}
				</div>
			</div>

			<div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pr-1">
				{filtered.map((sb) => {
					const isSelected = selected.includes(sb.value);
					return (
						<div
							key={sb.value}
							onClick={() => toggle(sb.value)}
							className={`ob-book-tile ${isSelected ? 'ob-book-tile-selected' : ''}`}>
							<div style={{ width: 32, height: 32, flexShrink: 0 }}>
								<LogoGenerator sportsbook={sb.value} customSize={32} />
							</div>
							<div className="text-[13px] truncate">{sb.label}</div>
							{isSelected && <div className="ob-book-check">&#10003;</div>}
						</div>
					);
				})}
				{filtered.length === 0 && (
					<div className="col-span-full text-center text-sm text-[#8a90a2] py-8">No sportsbooks match "{query}"</div>
				)}
			</div>
		</OnboardingShell>
	);
};
